/**
 * JSON-LD schema builders for SEO pages.
 * Each helper returns a plain JS object; serialize with JSON.stringify
 * inside route head().scripts.
 */

const SITE_URL = "https://heallexa.com";

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Heallexa",
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    sameAs: [],
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Heallexa",
    url: SITE_URL,
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

export function breadcrumbListSchema(items: Array<{ name: string; url: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url.startsWith("http") ? it.url : `${SITE_URL}${it.url}`,
    })),
  };
}

export function faqPageSchema(qa: Array<{ q: string; a: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: qa.map((x) => ({
      "@type": "Question",
      name: x.q,
      acceptedAnswer: { "@type": "Answer", text: x.a },
    })),
  };
}

export function collectionPageSchema(opts: {
  name: string;
  description?: string;
  url: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: opts.name,
    description: opts.description,
    url: opts.url.startsWith("http") ? opts.url : `${SITE_URL}${opts.url}`,
  };
}

export function itemListSchema(items: Array<{ name: string; url: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      url: it.url.startsWith("http") ? it.url : `${SITE_URL}${it.url}`,
    })),
  };
}

// ── Specialty type sets ───────────────────────────────────────────────────────
// `specialty` is free text pulled directly from the federal NPI registry at
// import time. The previous keyword sets only covered specialties pulled
// from a handful of ad hoc examples, so most of the 50 specialty categories
// Heallexa actually organizes providers under — Audiologists, Eye Doctors /
// Optometrists, Midwives, Naturopathic Doctors, Nurse Practitioners,
// Occupational Therapists, Physical Therapy, Podiatrists, Speech
// Therapists, Sports Medicine, Veterinarians, Women's Health, Dietitians /
// Nutritionists, and more — never matched any bucket and fell through to
// the generic "MedicalBusiness" type, even though Schema.org has a
// perfectly good, valid type for nearly all of them (VeterinaryCare for
// vets, MedicalClinic for allied-health/clinic-style practices). That left
// the large majority of providers without the more specific type Google
// rich results look for.

const DENTAL_TYPES = new Set([
  "dentist", "dental", "orthodontist", "endodontist",
  "oral surgeon", "periodontist", "prosthodontist", "pedodontist",
]);

const VETERINARY_TYPES = new Set(["veterinar"]);

const PHYSICIAN_TYPES = new Set([
  "physician", "doctor", "primary care", "internal medicine",
  "family medicine", "family doctor", "general practitioner", "general practice",
  "pediatrician", "cardiologist", "dermatologist",
  "psychiatrist", "neurologist", "ophthalmologist", "optometr", "eye doctor",
  "orthopedic surgeon", "orthopaedic",
  "obstetrician", "gynecologist", "women's health", "urologist", "gastroenterologist",
  "pulmonologist", "rheumatologist", "endocrinologist", "oncologist",
  "hematologist", "nephrologist", "allergist", "immunologist",
  "infectious disease", "radiologist", "anesthesiologist", "surgeon",
  "geriatrician", "naturopath", "nurse practitioner", "midwife", "midwives",
  "sports medicine", "pain medicine", "pain management",
]);

const MENTAL_HEALTH_TYPES = new Set([
  "therapist", "psychologist", "counselor", "counselling",
  "psychiatry", "behavioral health", "mental health", "social worker",
  "marriage and family", "lmft", "lcsw", "licensed clinical",
]);

const EMERGENCY_TYPES = new Set([
  "urgent care", "emergency", "emergency medicine", "er ", "walk-in",
]);

// Allied-health / clinic-style providers that don't fit Physician, Dentist,
// or VeterinaryCare, but are still far better described by Schema.org's
// "MedicalClinic" type than the bare "MedicalBusiness" fallback: audiology,
// dietetics/nutrition, occupational and physical therapy, podiatry,
// speech-language pathology, chiropractic.
const CLINIC_TYPES = new Set([
  "audiolog", "dietit", "nutrition", "occupational therap", "physical therap",
  "podiatr", "foot", "ankle", "speech", "chiropract",
]);

// ── providerSchemaType ────────────────────────────────────────────────────────

/**
 * Maps a provider specialty string to the most specific Schema.org @type.
 * Returns an array when dual-typing benefits rich result eligibility:
 *
 * - ["Dentist", "MedicalBusiness"]         → dental knowledge panel + local business rich result
 * - ["VeterinaryCare", "MedicalBusiness"]   → veterinary local business rich result
 * - ["Physician", "MedicalBusiness"]        → physician knowledge panel + local business rich result
 *                                             (also used for mental health providers — no
 *                                             dedicated Schema.org subtype exists for them)
 * - ["MedicalBusiness", "EmergencyService"] → urgent care / emergency walk-ins
 * - ["MedicalClinic", "MedicalBusiness"]     → allied-health / clinic-style practices
 * - "MedicalBusiness"                       → anything that matches none of the above
 *
 * JSON-LD @type accepts both a string and an array, so the output
 * can be assigned directly without changes in the consuming route.
 */
export function providerSchemaType(specialty: string | null | undefined): string | string[] {
  const s = (specialty ?? "").toLowerCase();

  for (const k of DENTAL_TYPES)        if (s.includes(k)) return ["Dentist", "MedicalBusiness"];
  for (const k of VETERINARY_TYPES)    if (s.includes(k)) return ["VeterinaryCare", "MedicalBusiness"];
  for (const k of PHYSICIAN_TYPES)     if (s.includes(k)) return ["Physician", "MedicalBusiness"];
  for (const k of MENTAL_HEALTH_TYPES) if (s.includes(k)) return ["Physician", "MedicalBusiness"];
  for (const k of EMERGENCY_TYPES)     if (s.includes(k)) return ["MedicalBusiness", "EmergencyService"];
  for (const k of CLINIC_TYPES)        if (s.includes(k)) return ["MedicalClinic", "MedicalBusiness"];

  return "MedicalBusiness";
}

export function providerSchema(p: {
  id: string;
  name: string;
  specialty?: string | null;
  practice?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  phone?: string | null;
  website?: string | null;
  photoUrl?: string | null;
  rating?: number | null;
  reviewCount?: number | null;
}) {
  const type = providerSchemaType(p.specialty);
  const url = `${SITE_URL}/providers/${p.id}`;
  const obj: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": type,
    name: p.name,
    url,
  };
  if (p.specialty) obj.medicalSpecialty = p.specialty;
  if (p.practice) obj.parentOrganization = { "@type": "Organization", name: p.practice };
  if (p.photoUrl) obj.image = p.photoUrl;
  if (p.phone) obj.telephone = p.phone;
  if (p.website) obj.sameAs = [p.website];
  if (p.address || p.city) {
    obj.address = {
      "@type": "PostalAddress",
      streetAddress: p.address ?? undefined,
      addressLocality: p.city ?? undefined,
      addressRegion: p.state ?? undefined,
      postalCode: p.zip ?? undefined,
      addressCountry: "US",
    };
  }
  if (typeof p.rating === "number" && p.rating > 0) {
    obj.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: p.rating,
      reviewCount: Math.max(1, p.reviewCount ?? 1),
    };
  }
  return obj;
}

/** Convenience: returns a route head().scripts entry. */
export function jsonLdScript(data: unknown) {
  return {
    type: "application/ld+json",
    children: JSON.stringify(data),
  } as const;
}
